import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import Header from "@/components/Header";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { fmtMoney, CURRENCIES, currencyInfo } from "@/lib/currency";
import { api, formatApiError } from "@/lib/api";
import { Calculator, DollarSign, Info, Plus, Trash2, Package, X } from "lucide-react";

export default function CostingCalculator() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [productName, setProductName] = useState("Dozen Sourdough Loaves");
  const [batchSize, setBatchSize] = useState(12); // units produced per batch
  const [ingredients, setIngredients] = useState([
    { id: 1, name: "Bread flour", cost: 6.00 },
    { id: 2, name: "Water/salt/starter", cost: 0.50 },
  ]);
  const [nextId, setNextId] = useState(3);
  const [inventory, setInventory] = useState([]);
  const [showImport, setShowImport] = useState(false);
  const [importSelection, setImportSelection] = useState({}); // {invId: qtyUsedThisBatch}
  const [packaging, setPackaging] = useState(0.85);        // per unit
  const [laborHours, setLaborHours] = useState(3);
  const [laborRate, setLaborRate] = useState(20);           // hourly wage
  const [utilities, setUtilities] = useState(1.50);         // per batch (oven, electricity)
  const [otherOverhead, setOtherOverhead] = useState(0);   // per batch
  const [sellingPrice, setSellingPrice] = useState(12);    // per unit
  const [currency, setCurrency] = useState(user?.currency || "USD");

  useEffect(() => {
    if (loading) return;
    if (!user || user === false) { nav("/login"); return; }
    if (user.currency) setCurrency(user.currency);
    api.get("/inventory").then((r) => setInventory(r.data.filter((i) => i.category === "ingredient" && (i.cost_per_unit || 0) > 0))).catch(() => {});
  }, [user, loading, nav]);

  const ingCostBatch = useMemo(() => ingredients.reduce((s, i) => s + (parseFloat(i.cost) || 0), 0), [ingredients]);
  const laborCostBatch = (parseFloat(laborHours) || 0) * (parseFloat(laborRate) || 0);
  const packagingBatch = (parseFloat(packaging) || 0) * (parseFloat(batchSize) || 0);
  const totalCostBatch = ingCostBatch + laborCostBatch + packagingBatch + (parseFloat(utilities) || 0) + (parseFloat(otherOverhead) || 0);
  const bs = parseFloat(batchSize) || 1;
  const costPerUnit = totalCostBatch / bs;
  const revenueBatch = (parseFloat(sellingPrice) || 0) * bs;
  const profitBatch = revenueBatch - totalCostBatch;
  const profitPerUnit = profitBatch / bs;
  const margin = revenueBatch > 0 ? (profitBatch / revenueBatch) * 100 : 0;
  const marginColor = margin < 20 ? "#C2185B" : margin < 40 ? "#F4A83B" : "#4C9F70";

  const addIng = () => { setIngredients([...ingredients, { id: nextId, name: "", cost: 0 }]); setNextId(nextId + 1); };

  const applyImport = () => {
    const rows = [];
    let n = nextId;
    for (const [invId, qtyStr] of Object.entries(importSelection)) {
      const qty = parseFloat(qtyStr) || 0;
      if (qty <= 0) continue;
      const item = inventory.find((i) => i.id === invId);
      if (!item) continue;
      const cost = qty * (item.cost_per_unit || 0);
      rows.push({ id: n++, name: `${item.name} (${qty}${item.unit})`, cost: Number(cost.toFixed(4)) });
    }
    if (!rows.length) { toast.error("Enter a quantity for at least one ingredient"); return; }
    setIngredients([...ingredients, ...rows]);
    setNextId(n);
    setShowImport(false);
    setImportSelection({});
    toast.success(`Added ${rows.length} ingredient row${rows.length > 1 ? "s" : ""} from inventory`);
  };

  if (loading || !user) return null;

  return (
    <>
      <Header />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 lg:py-10">
        <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-[#857163]">
              <Calculator className="w-3.5 h-3.5" style={{ color: "#F4A83B" }} /><span>Business tool</span>
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-[#3B2C24] mt-1">
              Full Costing & <span style={{ color: "#F4A83B" }}>Profit</span> Calculator
            </h1>
            <p className="mt-2 text-[#857163] max-w-2xl">True cost per item — including labor, packaging, and utilities — plus your profit margin at any price point.</p>
          </div>
          <select value={currency} onChange={(e) => setCurrency(e.target.value)} className="cp-input !py-1.5 !text-xs max-w-[140px]" data-testid="currency-select">
            {CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.symbol} {c.code}</option>)}
          </select>
        </div>

        <div className="grid lg:grid-cols-[1fr_360px] gap-6">
          <div className="cp-card p-5 sm:p-6 space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <label><span className="text-xs text-[#857163]">Product name</span><input className="cp-input mt-1" value={productName} onChange={(e) => setProductName(e.target.value)} data-testid="product-name" /></label>
              <label><span className="text-xs text-[#857163]">Batch size (units)</span><input type="number" min={1} className="cp-input mt-1" value={batchSize} onChange={(e) => setBatchSize(e.target.value)} data-testid="batch-size" /></label>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-medium text-[#3B2C24]">Ingredient costs per batch</div>
                {inventory.length > 0 && (
                  <button onClick={() => setShowImport(true)} className="text-xs inline-flex items-center gap-1 text-[#8EAC50] hover:underline" data-testid="import-inventory-btn">
                    <Package className="w-3.5 h-3.5" /> Import from Inventory
                  </button>
                )}
              </div>
              <div className="space-y-2">
                {ingredients.map((i) => (
                  <div key={i.id} className="grid grid-cols-[1fr_120px_36px] gap-2" data-testid={`ing-${i.id}`}>
                    <input className="cp-input !py-1.5 !text-sm" placeholder="Ingredient" value={i.name} onChange={(e) => setIngredients(ingredients.map((x) => x.id === i.id ? { ...x, name: e.target.value } : x))} />
                    <input type="number" step={0.01} min={0} className="cp-input !py-1.5 !text-sm" value={i.cost} onChange={(e) => setIngredients(ingredients.map((x) => x.id === i.id ? { ...x, cost: e.target.value } : x))} />
                    <button onClick={() => setIngredients(ingredients.filter((x) => x.id !== i.id))} className="grid place-items-center rounded-full hover:bg-red-50 text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
                <button onClick={addIng} className="text-sm text-[#857163] hover:bg-[#F9EFE6] py-2 w-full rounded-2xl border border-dashed border-[#EBDCD1] inline-flex items-center justify-center gap-1.5" data-testid="add-ing"><Plus className="w-3.5 h-3.5" /> Add ingredient</button>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-3 pt-3 border-t border-[#EBDCD1]">
              <label><span className="text-xs text-[#857163]">Packaging cost / unit</span><input type="number" step={0.01} min={0} className="cp-input mt-1" value={packaging} onChange={(e) => setPackaging(e.target.value)} data-testid="packaging" /></label>
              <label><span className="text-xs text-[#857163]">Utilities / batch (oven, water)</span><input type="number" step={0.01} min={0} className="cp-input mt-1" value={utilities} onChange={(e) => setUtilities(e.target.value)} data-testid="utilities" /></label>
              <label><span className="text-xs text-[#857163]">Labor hours / batch</span><input type="number" step={0.25} min={0} className="cp-input mt-1" value={laborHours} onChange={(e) => setLaborHours(e.target.value)} data-testid="labor-hours" /></label>
              <label><span className="text-xs text-[#857163]">Hourly rate</span><input type="number" step={0.5} min={0} className="cp-input mt-1" value={laborRate} onChange={(e) => setLaborRate(e.target.value)} data-testid="labor-rate" /></label>
              <label className="sm:col-span-2"><span className="text-xs text-[#857163]">Other overhead / batch (rent, insurance, licenses)</span><input type="number" step={0.5} min={0} className="cp-input mt-1" value={otherOverhead} onChange={(e) => setOtherOverhead(e.target.value)} data-testid="overhead" /></label>
            </div>

            <div className="pt-3 border-t border-[#EBDCD1]">
              <label className="block"><span className="text-xs text-[#857163]">Selling price per unit</span>
                <input type="number" step={0.25} min={0} className="cp-input mt-1" value={sellingPrice} onChange={(e) => setSellingPrice(e.target.value)} data-testid="selling-price" />
              </label>
            </div>
          </div>

          <div className="cp-card p-5 sm:p-6 sticky top-24 self-start space-y-3" data-testid="costing-results">
            <div className="text-xs uppercase tracking-widest text-[#857163]">Break down for</div>
            <div className="font-heading text-xl text-[#3B2C24] leading-tight">{productName}</div>
            <div className="pt-2 space-y-1.5 text-sm">
              <Row label="Ingredients" value={fmtMoney(ingCostBatch, currency)} />
              <Row label={`Labor (${laborHours}h × ${fmtMoney(laborRate, currency)})`} value={fmtMoney(laborCostBatch, currency)} />
              <Row label={`Packaging (${batchSize} × ${fmtMoney(parseFloat(packaging) || 0, currency)})`} value={fmtMoney(packagingBatch, currency)} />
              <Row label="Utilities" value={fmtMoney(parseFloat(utilities) || 0, currency)} />
              <Row label="Overhead" value={fmtMoney(parseFloat(otherOverhead) || 0, currency)} />
              <div className="border-t border-[#EBDCD1] pt-1.5"><Row label="Total batch cost" value={fmtMoney(totalCostBatch, currency)} strong /></div>
            </div>

            <div className="grid grid-cols-2 gap-2 pt-2">
              <Metric label="Cost / unit" value={fmtMoney(costPerUnit, currency)} accent="#3B2C24" />
              <Metric label="Profit / unit" value={fmtMoney(profitPerUnit, currency)} accent={profitPerUnit >= 0 ? "#4C9F70" : "#C2185B"} />
              <Metric label="Batch revenue" value={fmtMoney(revenueBatch, currency)} accent="#3B2C24" />
              <Metric label="Batch profit" value={fmtMoney(profitBatch, currency)} accent={profitBatch >= 0 ? "#4C9F70" : "#C2185B"} />
            </div>

            <div className="p-3 rounded-2xl" style={{ background: `${marginColor}18` }}>
              <div className="text-xs uppercase tracking-widest text-[#857163]">Profit margin</div>
              <div className="font-heading text-3xl font-bold mt-1" style={{ color: marginColor }}>{margin.toFixed(1)}%</div>
              <div className="text-[11px] text-[#857163] mt-1">
                {margin < 20 ? "Consider raising your price or trimming costs." : margin < 40 ? "Decent margin — some room to grow." : "Healthy margin!"}
              </div>
            </div>

            <div className="text-[10px] text-[#857163] flex gap-1.5"><Info className="w-3 h-3 mt-0.5" /> This does not include taxes or sales fees. Consult a bookkeeper for full accounting.</div>
          </div>
        </div>
      </main>

      {showImport && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowImport(false)} data-testid="import-modal">
          <div className="bg-white rounded-3xl w-full max-w-lg max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-[#EBDCD1] flex items-start justify-between">
              <div>
                <div className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-[#857163]"><Package className="w-3.5 h-3.5" style={{ color: "#8EAC50" }} /><span>Inventory</span></div>
                <h2 className="font-heading text-xl text-[#3B2C24] mt-1">Import ingredients into this batch</h2>
                <p className="text-xs text-[#857163] mt-1">Enter how much of each item this batch uses. Cost is computed from your inventory's cost-per-unit.</p>
              </div>
              <button onClick={() => setShowImport(false)} className="p-1.5 hover:bg-[#F9EFE6] rounded-full"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-5 overflow-y-auto space-y-2">
              {inventory.map((item) => (
                <div key={item.id} className="grid grid-cols-[1fr_120px] gap-3 items-center" data-testid={`inv-row-${item.id}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-[#3B2C24] truncate">{item.name}</div>
                    <div className="text-xs text-[#857163]">{fmtMoney(item.cost_per_unit, currency)} / {item.unit} · stock: {item.current_qty}{item.unit}</div>
                  </div>
                  <div className="relative">
                    <input
                      type="number" step={0.1} min={0}
                      className="cp-input !py-1.5 !text-sm pr-10"
                      placeholder="0"
                      value={importSelection[item.id] || ""}
                      onChange={(e) => setImportSelection({ ...importSelection, [item.id]: e.target.value })}
                      data-testid={`inv-qty-${item.id}`}
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#857163]">{item.unit}</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="p-4 border-t border-[#EBDCD1] flex justify-end gap-2">
              <button onClick={() => setShowImport(false)} className="cp-btn-ghost">Cancel</button>
              <button onClick={applyImport} className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full text-white font-semibold text-sm hover:opacity-90" style={{ background: "#8EAC50" }} data-testid="apply-import">Add to recipe</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Row({ label, value, strong }) {
  return (
    <div className={`flex items-center justify-between ${strong ? "font-semibold text-[#3B2C24]" : "text-[#3B2C24]"}`}>
      <span>{label}</span><span className="font-mono tabular-nums">{value}</span>
    </div>
  );
}
function Metric({ label, value, accent }) {
  return <div className="p-3 rounded-2xl border border-[#EBDCD1]"><div className="text-[10px] uppercase tracking-widest text-[#857163]">{label}</div><div className="font-heading text-lg font-bold mt-0.5 tabular-nums" style={{ color: accent }}>{value}</div></div>;
}
